/**
 * Convert an ExcelJS workbook into a Univer IWorkbookData snapshot (client-side spike).
 * Full Excel style fidelity would need Univer Pro exchange; this maps values + basic fonts/fills.
 *
 * @param {import('exceljs').Workbook} workbook
 * @returns {object}
 */
export function exceljsWorkbookToUniverData(workbook) {
    const sheets = {};
    const sheetOrder = [];
    /** @type {Record<string, object>} */
    const styles = {};
    /** @type {Map<string, string>} */
    const styleKeyToId = new Map();
    let styleSeq = 0;

    /**
     * @param {object|null|undefined} style
     * @returns {string|undefined}
     */
    function styleIdFor(style) {
        if (!style) {
            return undefined;
        }

        const key = JSON.stringify(style);
        const existing = styleKeyToId.get(key);
        if (existing) {
            return existing;
        }

        const id = `s${styleSeq++}`;
        styleKeyToId.set(key, id);
        styles[id] = style;
        return id;
    }

    workbook.eachSheet((worksheet, sheetId) => {
        const id = `sheet-${sheetId}`;
        sheetOrder.push(id);

        /** @type {Record<number, Record<number, object>>} */
        const cellData = {};
        let maxRow = 0;
        let maxCol = 0;

        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            const r = rowNumber - 1;
            maxRow = Math.max(maxRow, r);
            cellData[r] = cellData[r] || {};

            row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                const c = colNumber - 1;
                maxCol = Math.max(maxCol, c);

                const value = normalizeExcelCellValue(cell.value);
                const styleId = styleIdFor(mapExcelCellStyle(cell));

                /** @type {{ v?: string|number|boolean, t?: number, s?: string }} */
                const cellPayload = {};
                if (value !== null && value !== undefined && value !== '') {
                    cellPayload.v = value;
                    if (typeof value === 'number') {
                        cellPayload.t = 2;
                    } else if (typeof value === 'boolean') {
                        cellPayload.t = 3;
                    } else {
                        cellPayload.t = 1;
                    }
                }
                if (styleId) {
                    cellPayload.s = styleId;
                }

                if (Object.keys(cellPayload).length) {
                    cellData[r][c] = cellPayload;
                }
            });
        });

        sheets[id] = {
            id,
            name: worksheet.name || `Sheet${sheetId}`,
            tabColor: '',
            hidden: 0,
            rowCount: Math.max(maxRow + 20, 40),
            columnCount: Math.max(maxCol + 10, 20),
            zoomRatio: 1,
            scrollTop: 0,
            scrollLeft: 0,
            defaultColumnWidth: 88,
            defaultRowHeight: 24,
            mergeData: [],
            cellData,
            rowData: {},
            columnData: {},
            showGridlines: 1,
            rowHeader: { width: 46, hidden: 0 },
            columnHeader: { height: 20, hidden: 0 },
            selections: ['A1'],
            rightToLeft: 0,
        };
    });

    return {
        id: 'file-viewer-xlsx',
        name: workbook.title || 'Workbook',
        appVersion: '0.10.0',
        locale: 'enUS',
        styles,
        sheetOrder,
        sheets,
    };
}

/**
 * @param {unknown} value
 * @returns {string|number|boolean|null}
 */
function normalizeExcelCellValue(value) {
    if (value == null) {
        return null;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (value instanceof Date) {
        return value.toLocaleString();
    }

    if (typeof value === 'object') {
        if ('richText' in value && Array.isArray(value.richText)) {
            return value.richText.map(part => part.text || '').join('');
        }
        if ('text' in value && typeof value.text === 'string') {
            return value.text;
        }
        if ('result' in value && value.result != null) {
            return normalizeExcelCellValue(value.result);
        }
        if ('formula' in value) {
            return value.result != null ? normalizeExcelCellValue(value.result) : String(value.formula);
        }
        if ('hyperlink' in value) {
            return value.text || value.hyperlink || '';
        }
        if ('error' in value) {
            return String(value.error);
        }
    }

    return String(value);
}

/**
 * @param {import('exceljs').Cell} cell
 * @returns {object|null}
 */
function mapExcelCellStyle(cell) {
    const font = cell.font || {};
    const fill = cell.fill || {};
    const alignment = cell.alignment || {};

    /** @type {Record<string, unknown>} */
    const style = {};

    if (font.bold) style.bl = 1;
    if (font.italic) style.it = 1;
    if (font.underline) style.ul = { s: 1 };
    if (font.size) style.fs = font.size;
    if (font.name) style.ff = font.name;
    if (font.color?.argb) {
        style.cl = { rgb: argbToRgb(font.color.argb) };
    }

    if (fill.type === 'pattern' && fill.fgColor?.argb) {
        style.bg = { rgb: argbToRgb(fill.fgColor.argb) };
    }

    if (alignment.horizontal === 'center') style.ht = 2;
    if (alignment.horizontal === 'right') style.ht = 3;
    if (alignment.horizontal === 'left') style.ht = 1;
    if (alignment.vertical === 'middle') style.vt = 2;
    if (alignment.vertical === 'top') style.vt = 1;
    if (alignment.vertical === 'bottom') style.vt = 3;
    if (alignment.wrapText) style.tb = 3;

    return Object.keys(style).length ? style : null;
}

/**
 * @param {string} argb
 * @returns {string}
 */
function argbToRgb(argb) {
    const cleaned = String(argb).replace(/^#/, '');
    if (cleaned.length === 8) {
        return `#${cleaned.slice(2)}`;
    }
    if (cleaned.length === 6) {
        return `#${cleaned}`;
    }
    return '#000000';
}
